import type * as THREE from 'three/webgpu';

type N = any;

/**
 * Makes the renderer compile render pipelines in the background.
 *
 * three.js creates an object's pipeline the first time it draws it, and by default does so
 * synchronously: the GPU process then compiles the shaders before it presents another
 * frame. Walking into a hollow whose shaders hadn't been used yet froze the game for
 * seconds. With this, a new pipeline is created with `createRenderPipelineAsync` (the path
 * three.js uses for `compileAsync`) and three.js simply skips drawing the object until it is
 * ready (`Pipelines.isReady`), so at worst something appears a moment late.
 *
 * `pending` counts compiles still running; `started` and `finished` give progress.
 */
export class AsyncPipelines {
  started = 0;
  finished = 0;

  constructor(renderer: THREE.WebGPURenderer) {
    const pipelines = (renderer as N)._pipelines;
    const getForRender = pipelines.getForRender.bind(pipelines);
    // three.js pushes each pending compile's promise here (null means "compile now").
    const collector = {
      push: (p: Promise<unknown>) => {
        this.started++;
        p.finally(() => {
          this.finished++;
        });
        return 0;
      },
    };
    pipelines.getForRender = (renderObject: unknown, promises: unknown = null) =>
      getForRender(renderObject, promises ?? collector);
  }

  get pending(): number {
    return this.started - this.finished;
  }
}
